import { fabric } from "fabric";
import { ControllerOptions } from "..";
import { isAdsEditor } from "../../../utils";
import { logoConfigLabel } from "../../hooks/useAdsEditorUtils";
import { OBJECT_TYPES } from "../../views/DesignEditor/components/ContextMenu/ContextMenusData";
import { ObjectsEnum } from "../../views/DesignEditor/components/Panels/panelItems";
import { LayerType } from "../common/constants";
import shourcutsManager from "../utils/shourcutsManager";
import Base from "./Base";

class Events extends Base {
	constructor(props: ControllerOptions) {
		super(props);
		this.initialize();
	}

	private initialize() {
		this.canvas.wrapperEl.tabIndex = 1;
		this.canvas.wrapperEl.style.outline = "none";
		// @ts-ignore
		this.canvas.on({
			"mouse:dblclick": this.onDoubleClick,
			"mouse:down": this.onMouseDown,
			"mouse:up": this.onMouseUp,
			"mouse:move": this.onMouseMove,
			"selection:cleared": this.handleSelection,
			"selection:updated": this.handleSelection,
			"mouse:wheel": this.onMouseWheel,
			"mouse:out": this.onMouseOut,
			"object:modified": this.objectModified,
			"background:selected": this.onBackgroundSelected,
			"object:added": this.objectAdded,
			"mouse:over": this.onMouseOver,
		});

		this.canvas.wrapperEl.addEventListener("keydown", this.onKeyDown.bind(this), false);
		this.canvas.wrapperEl.addEventListener("mousedown", this.handleNativeMouseDown);
		this.canvas.wrapperEl.addEventListener("keyup", this.onKeyUp.bind(this), false);
		window.addEventListener("mouseup", this.handleNativeMouseUp);
	}

	public destroy() {
		this.canvas.off({
			"mouse:dblclick": this.onDoubleClick,
			"mouse:down": this.onMouseDown,
			"mouse:up": this.onMouseUp,
			"mouse:move": this.onMouseMove,
			"selection:cleared": this.handleSelection,
			"selection:updated": this.handleSelection,
			"mouse:wheel": this.onMouseWheel,
			"mouse:out": this.onMouseOut,
			"object:modified": this.objectModified,
			"background:selected": this.onBackgroundSelected,
			"object:added": this.objectAdded,
			"mouse:over": this.onMouseOver,
		});

		this.canvas.wrapperEl.removeEventListener("keydown", this.onKeyDown.bind(this));
		this.canvas.wrapperEl.removeEventListener("mousedown", this.handleNativeMouseDown);
		this.canvas.wrapperEl.removeEventListener("keyup", this.onKeyUp.bind(this));
		window.removeEventListener("mouseup", this.handleNativeMouseUp);
	}

	private onDoubleClick = (event: fabric.IEvent<any>) => {
		const subTarget = event.subTargets![0];
		if (subTarget) {
			this.editor.objects.select(subTarget.id);
		}
	};

	private onMouseDown = (e: fabric.IEvent<any>) => {
		this.editor.objects.pasteStyle();
		if (e.button === 3) {
			this.state.setContextMenuRequest({ left: e.e.offsetX, top: e.e.offsetY, target: e.target });
		} else {
			this.state.setContextMenuRequest(null);
		}
	};
	// Handle mouse down event to initiate panning when:
	// - Middle mouse button is pressed
	// - Space key is held and left-click is pressed
	private handleNativeMouseDown = (e: MouseEvent) => {
		if (e.button === 1 || (this.editor.zoom.isSpacePressed && e.button === 0)) {
			e.preventDefault();
			this.editor.zoom.isPanning = true;
			this.editor.zoom.lastPosX = e.clientX;
			this.editor.zoom.lastPosY = e.clientY;
			this.canvas.setCursor("grab");
			this.canvas.renderAll();
			return;
		}
	};

	private handleNativeMouseUp = (e: MouseEvent) => {
		if (e.button === 1 && this.editor.zoom.isPanning) {
			this.editor.zoom.isPanning = false;
			this.canvas.setCursor("default");
			this.canvas.renderAll();
		}
	};

	// Handle mouse move to perform panning if panning mode is active
	private onMouseMove = (e: fabric.IEvent<MouseEvent>) => {
		if (!this.editor.zoom.isPanning) return;

		const deltaX = e.e.clientX - this.editor.zoom.lastPosX;
		const deltaY = e.e.clientY - this.editor.zoom.lastPosY;

		this.canvas.relativePan(new fabric.Point(deltaX, deltaY));

		this.editor.zoom.lastPosX = e.e.clientX;
		this.editor.zoom.lastPosY = e.e.clientY;
	};

	// Handle mouse up to stop panning and reset cursor
	private onMouseUp = (_e: fabric.IEvent<MouseEvent>) => {
		if (this.editor.zoom.isPanning) {
			this.editor.zoom.isPanning = false;
			this.canvas.setCursor("default");
			this.canvas.renderAll();
			return;
		}

		// If not panning, handle regular selection
		this.handleSelection(_e);
	};

	// Handle key up to stop space-triggered panning
	private onKeyUp = (e: KeyboardEvent) => {
		if (e.code === "Space") {
			this.editor.zoom.isSpacePressed = false;

			// Stop panning if space is released while panning
			if (this.editor.zoom.isPanning) {
				this.editor.zoom.isPanning = false;
				this.canvas.setCursor("default");
				this.canvas.renderAll();
			}
		}
	};

	objectModified = (event: fabric.IEvent & { invokeSave: boolean }) => {
		const { target } = event;
		const { invokeSave = true } = event;
		if (target instanceof fabric.Textbox) {
			this.scaleTextbox(target);
		}
		if (invokeSave) this.editor.history.save();
	};

	objectAdded = (event: fabric.IEvent) => {
		const { target } = event;
		if (this.state.isPopupView) {
			if (target instanceof fabric.Object) {
				this.editor.objects.lockObject(target, this.state.isPopupView);
			}

			if (
				target instanceof fabric.Object &&
				target?.type !== LayerType.STATIC_TEXT &&
				target?.type !== LayerType.STATIC_IMAGE
			) {
				target.selectable = false;
			}
		}
	};

	onMouseOut = () => {
		const active = this.editor?.canvas?.canvas.getActiveObject();
		if (
			active &&
			active?.type === LayerType.STATIC_TEXT &&
			active.fixedSize &&
			active.fixedWidthValue &&
			active.fixedHeightValue &&
			active.width &&
			active.height &&
			active.fontSize
		) {
			active.set("width", active.fixedWidthValue);
			active.set("height", active.fixedHeightValue);
		}
		this.canvas.renderAll();
	};
	onMouseOver = (event: fabric.IEvent<any>) => {
		const { target } = event;
		if (target instanceof fabric.Object && target?.type === LayerType.STATIC_TEXT && target.fixedSize) {
			target.set("height", target.fixedHeightValue);
		}
	};

	onMouseWheel = (event: fabric.IEvent<any>) => {
		const isCtrlKey = event.e.ctrlKey;
		if (isCtrlKey) {
			this.handleZoom(event);
		}
	};

	handleZoom = (event: fabric.IEvent<any>) => {
		const delta = event.e.deltaY;
		let zoomRatio = this.canvas.getZoom();
		if (delta > 0) {
			zoomRatio -= 0.02;
		} else {
			zoomRatio += 0.02;
		}
		this.editor.zoom.zoomToPoint(
			new fabric.Point(this.canvas.getWidth() / 2, this.canvas.getHeight() / 2),
			zoomRatio,
		);
		event.e.preventDefault();
		event.e.stopPropagation();
	};

	onKeyDown(event: KeyboardEvent) {
		const currentScene = this.editor?.scene.exportToJSON();
		let usedConfigs: Set<string> | undefined = undefined;
		let activeObject = this.canvas.getActiveObject();
		let firstAvailableConfig = undefined;
		if (shourcutsManager.isCtrlOrCmdZero(event)) {
			event.preventDefault();
			this.editor.zoom.zoomToFit();
		} else if (shourcutsManager.isCtrlOrCmdMinus(event)) {
			event.preventDefault();
			this.editor.zoom.zoomIn();
		} else if (shourcutsManager.isCtrlOrCmdEqual(event)) {
			event.preventDefault();
			this.editor.zoom.zoomOut();
		} else if (shourcutsManager.isCtrlOrCmdOne(event)) {
			event.preventDefault();
			this.editor.zoom.zoomToOne();
		} else if (shourcutsManager.isCtrlOrCmdZ(event)) {
			this.editor.history.undo();
		} else if (shourcutsManager.isCtrlOrCmdShiftZ(event)) {
			this.editor.history.redo();
		} else if (shourcutsManager.isCtrlOrCmdY(event)) {
			this.editor.history.redo();
		} else if (shourcutsManager.isCtrlOrCmdA(event)) {
			event.preventDefault();
			this.editor.objects.select();
		} else if (shourcutsManager.isDelete(event)) {
			event.preventDefault();
			const isRectangleObject = (object: fabric.Object) =>
				[ObjectsEnum.InnerRectangle as string, ObjectsEnum.OuterRectangle as string].includes(object.id);
			if (activeObject?.name === LayerType.GROUP.toLowerCase()) {
				activeObject._objects?.some((item) => {
					if (isRectangleObject(item)) {
						this.editor.objects.remove(ObjectsEnum.SmartImage);
						this.imagesStore.resetSmartImage(this.state.isPopupView);
						this.setCurrentScn(this.editor.scene.exportToJSON());
						return;
					}
					return;
				});
			} else if (activeObject && isRectangleObject(activeObject)) {
				this.editor.objects.remove(ObjectsEnum.InnerRectangle);
				this.imagesStore.resetSmartImage(this.state.isPopupView);
				this.setCurrentScn(this.editor.scene.exportToJSON());
				return;
			}
			if (this?.canvas?._activeObject.name !== OBJECT_TYPES.INITIAL_FRAME) {
				this.editor.objects.remove();
			}
		} else if (shourcutsManager.isCtrlOrCmdC(event)) {
			event.preventDefault();
			this.editor.objects.copy();
		} else if (shourcutsManager.isCtrlOrCmdV(event)) {
			event.preventDefault();
			if (currentScene && !isAdsEditor()) {
				usedConfigs = this.adsEditorUtils.getFabricLayerTakenConfigs(
					currentScene?.layers,
					activeObject?.type as LayerType,
					activeObject?.isLogo,
				);
				firstAvailableConfig = this.adsEditorUtils.getFirstAvailableConfig(
					usedConfigs,
					activeObject?.type as LayerType,
					activeObject?.isLogo ? logoConfigLabel : "",
				);
				if (!firstAvailableConfig) {
					this.campaignStore.setProperty("nonAvailableConfigSnackbarOpen", true);
					return;
				}
			}
			this.editor.objects.paste(firstAvailableConfig);
		} else if (shourcutsManager.isCtrlOrCmdX(event)) {
			event.preventDefault();
			this.editor.objects.cut();
		}
		if (event.code === "Space") {
			this.editor.zoom.isSpacePressed = true;
		}
	}

	onBackgroundSelected = () => {
		const objects = this.canvas.getObjects();
		const frame = objects[0];
		this.canvas.setActiveObject(objects[0]);
		this.state.setActiveObject(frame);
		this.canvas.requestRenderAll();
	};

	handleSelection = (target: fabric.IEvent) => {
		if (target) {
			this.state.setActiveObject(null);
			const initialSelection = this.canvas.getActiveObject() as any;
			const isNotMultipleSelection =
				(initialSelection && initialSelection.type === LayerType.GROUP.toLowerCase()) ||
				(initialSelection && initialSelection.type === LayerType.STATIC_VECTOR);

			if (initialSelection && !isNotMultipleSelection && initialSelection._objects) {
				const filteredObjects = (initialSelection._objects as fabric.Object[]).filter((object) => {
					if (object.type === LayerType.BACKGROUND) {
						return false;
					}
					return !object.locked;
				});
				this.canvas.discardActiveObject();
				if (filteredObjects.length > 0) {
					if (filteredObjects.length === 1) {
						this.canvas.setActiveObject(filteredObjects[0]);
						this.state.setActiveObject(filteredObjects[0]);
					} else {
						const activeSelection = new fabric.ActiveSelection(filteredObjects, {
							canvas: this.canvas,
						}) as fabric.Object;
						this.canvas.setActiveObject(activeSelection);
						this.state.setActiveObject(activeSelection);
					}
				}
			} else {
				this.state.setActiveObject(initialSelection);
			}
		} else {
			this.state.setActiveObject(null);
		}
		this.canvas.requestRenderAll();
	};

	scaleTextbox = (target: fabric.Textbox) => {
		const { fontSize, width, scaleX } = target;
		target.set({
			fontSize: fontSize! * scaleX!,
			width: width! * scaleX!,
			scaleX: 1,
			scaleY: 1,
		});
	};
}

export default Events;
